import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  DollarSign,
  AlertTriangle,
  User,
  Clock,
  FileText,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { ScrollArea } from '../ui/scroll-area';
import {
  OverdueGroup,
  OverdueTransaction,
  OverdueStatus,
  OverduePriority,
  RiskLevel,
  OVERDUE_STATUS_COLORS,
  OVERDUE_PRIORITY_COLORS,
  OVERDUE_STATUS_LABELS,
  OVERDUE_PRIORITY_LABELS,
  formatOverdueDays
} from '../../types/overdue';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';
import { TransactionDetailsModal } from './TransactionDetailsModal';

interface OverdueTableProps {
  data: OverdueGroup[];
  onSelectTransaction: (transactionId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  selectedTransactions: Set<string>;
  onRowClick?: (group: OverdueGroup) => void;
  onContactClient?: (clientId: string, method: 'email' | 'phone' | 'whatsapp') => void;
  onPriorityChange?: (transactionId: string, priority: OverduePriority) => void;
  onStatusChange?: (transactionId: string, status: OverdueStatus) => void;
  onSendNotice?: (clientId: string, method: 'email' | 'phone' | 'whatsapp', transactionIds?: string[]) => void;
  onAddNotes?: (transactionId: string, notes: string) => void;
  onMarkResolved?: (transactionId: string) => void;
  onScheduleFollowUp?: (transactionId: string, date: Date) => void;
  loading?: boolean;
  sortable?: boolean;
  title?: string;
  description?: string;
}

export function OverdueTable({
  data,
  onSelectTransaction,
  onSelectAll,
  selectedTransactions,
  onRowClick,
  onContactClient,
  onPriorityChange,
  onStatusChange,
  onSendNotice,
  onAddNotes,
  onMarkResolved,
  onScheduleFollowUp,
  loading = false,
  sortable = true,
  title = "Lançamentos em Atraso",
  description = "Organize e gerencie cobranças por cliente"
}: OverdueTableProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<OverdueGroup | null>(null);

  const toggleGroup = (clientId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };


  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  const getStatusBadge = (status: OverdueStatus) => {
    return (
      <Badge 
        variant="outline" 
        style={{ 
          borderColor: OVERDUE_STATUS_COLORS[status],
          color: OVERDUE_STATUS_COLORS[status],
          backgroundColor: `${OVERDUE_STATUS_COLORS[status]}10`
        }}
        className="text-xs"
      >
        {OVERDUE_STATUS_LABELS[status]}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: OverduePriority) => {
    return (
      <Badge 
        variant="outline"
        style={{ 
          borderColor: OVERDUE_PRIORITY_COLORS[priority],
          color: OVERDUE_PRIORITY_COLORS[priority],
          backgroundColor: `${OVERDUE_PRIORITY_COLORS[priority]}10`
        }}
        className="text-xs"
      >
        {OVERDUE_PRIORITY_LABELS[priority]}
      </Badge>
    );
  };

  const getGroupTotalSelected = (group: OverdueGroup) => {
    return group.transactions.filter(t => selectedTransactions.has(t.id)).length;
  };

  const isGroupFullySelected = (group: OverdueGroup) => {
    return group.transactions.every(t => selectedTransactions.has(t.id));
  };

  const isGroupPartiallySelected = (group: OverdueGroup) => {
    const selectedCount = getGroupTotalSelected(group);
    return selectedCount > 0 && selectedCount < group.transactions.length;
  };

  const handleGroupSelect = (group: OverdueGroup, selected: boolean) => {
    group.transactions.forEach(transaction => {
      onSelectTransaction(transaction.id, selected);
    });
  };

  const getRiskLevelColor = (client: any) => {
    switch (client.risk_level) {
      case 'baixo': return 'text-finance-green';
      case 'medio': return 'text-finance-amber';
      case 'alto': return 'text-finance-red';
      case 'muito_alto': return 'text-red-700';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-finance-green/10 flex items-center justify-center mb-4">
              <DollarSign className="h-8 w-8 text-finance-green" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nenhuma inadimplência encontrada</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Parabéns! Não há lançamentos em atraso no momento ou todos foram filtrados.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {data.length} cliente(s)
            </Badge>
            <Badge variant="outline" className="text-xs">
              {data.reduce((sum, group) => sum + group.transactions.length, 0)} lançamento(s)
            </Badge>
          </div>
        </div>

        {/* Select All */}
        <div className="flex items-center gap-3 pt-4">
          <Checkbox
            checked={selectedTransactions.size > 0}
            onCheckedChange={(checked) => onSelectAll(checked as boolean)}
            className="data-[state=checked]:bg-primary"
          />
          <span className="text-sm text-muted-foreground">
            {selectedTransactions.size > 0 
              ? `${selectedTransactions.size} lançamento(s) selecionado(s)`
              : 'Selecionar todos'
            }
          </span>
          {selectedTransactions.size > 0 && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={() => onSelectAll(false)}
              className="h-auto p-0 text-xs"
            >
              Desmarcar todos
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <ScrollArea className="h-[600px] w-full">
          <div className="space-y-2">
            {data.map((group) => {
              const isExpanded = expandedGroups.has(group.client.id);
              const selectedCount = getGroupTotalSelected(group);
              const isFullySelected = isGroupFullySelected(group);
              const isPartiallySelected = isGroupPartiallySelected(group);
              
              return (
                <Collapsible 
                  key={group.client.id}
                  open={isExpanded}
                  onOpenChange={() => toggleGroup(group.client.id)}
                >
                  {/* Client Header Row */}
                  <div className={cn(
                    "card-elevated border-0 shadow-sm hover:shadow-md transition-all duration-200",
                    isExpanded && "rounded-b-none border-b border-border/50"
                  )}>
                    <div className="p-6">
                      <div className="flex items-center gap-4">
                        {/* Selection */}
                        <div className="pt-1">
                          <Checkbox
                            checked={isFullySelected}
                            ref={(el) => {
                              if (el) el.indeterminate = isPartiallySelected;
                            }}
                            onCheckedChange={(checked) => handleGroupSelect(group, checked as boolean)}
                            className="data-[state=checked]:bg-primary"
                          />
                        </div>

                        {/* Expand/Collapse */}
                        <div className="pt-1">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="p-0 h-auto hover:bg-transparent">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>

                        {/* Client Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-4 mb-4">
                            <div className={cn(
                              "w-12 h-12 rounded-full flex items-center justify-center",
                              group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "bg-destructive/10" :
                              group.client.risk_level === RiskLevel.MEDIUM ? "bg-amber-500/10" :
                              "bg-success/10"
                            )}>
                              <User className={cn(
                                "h-6 w-6",
                                group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "text-destructive" :
                                group.client.risk_level === RiskLevel.MEDIUM ? "text-amber-600 dark:text-amber-400" :
                                "text-success"
                              )} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-xl truncate">
                                  {group.client.name}
                                </h3>
                                <span className={cn(
                                  "px-2 py-1 text-xs font-medium rounded-full",
                                  group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "bg-destructive/10 text-destructive" :
                                  group.client.risk_level === RiskLevel.MEDIUM ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" :
                                  "bg-success/10 text-success"
                                )}>
                                  {group.client.risk_level === RiskLevel.VERY_HIGH ? 'Muito Alto' :
                                   group.client.risk_level === RiskLevel.HIGH ? 'Alto' :
                                   group.client.risk_level === RiskLevel.MEDIUM ? 'Médio' : 'Baixo'} risco
                                </span>
                              </div>
                              {group.client.document && (
                                <p className="text-sm text-muted-foreground font-mono">
                                  {group.client.document}
                                </p>
                              )}
                            </div>

                            {/* Contact Methods */}
                            <div className="flex items-center gap-2">
                              {group.client.email && onContactClient && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onContactClient(group.client.id, 'email');
                                  }}
                                  className="h-9 px-3 gap-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                                  title="Enviar email"
                                >
                                  <Mail className="h-4 w-4" />
                                  <span className="hidden sm:inline">Email</span>
                                </Button>
                              )}
                              
                              {group.client.phone && onContactClient && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onContactClient(group.client.id, 'phone');
                                  }}
                                  className="h-9 px-3 gap-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                                  title="Ligar"
                                >
                                  <Phone className="h-4 w-4" />
                                  <span className="hidden sm:inline">Ligar</span>
                                </Button>
                              )}

                              {group.client.phone && onContactClient && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onContactClient(group.client.id, 'whatsapp');
                                  }}
                                  className="h-9 px-3 gap-2 hover:bg-green-600 hover:text-white transition-colors border-green-600 text-green-600"
                                  title="WhatsApp"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                  <span className="hidden sm:inline">WhatsApp</span>
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Client Summary */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-destructive/10 rounded-lg p-3 border border-destructive/20">
                              <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle className="h-4 w-4 text-destructive" />
                                <span className="text-xs text-destructive font-medium">Total em atraso</span>
                              </div>
                              <p className="text-lg font-bold text-foreground">
                                {formatCurrency(group.total_amount)}
                              </p>
                            </div>

                            <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="h-4 w-4 text-primary" />
                                <span className="text-xs text-primary font-medium">Lançamentos</span>
                              </div>
                              <p className="text-lg font-bold text-foreground">
                                {group.transactions.length}
                                {selectedCount > 0 && (
                                  <span className="text-sm text-primary ml-1">({selectedCount} sel)</span>
                                )}
                              </p>
                            </div>

                            <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
                              <div className="flex items-center gap-2 mb-1">
                                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Mais antigo</span>
                              </div>
                              <p className="text-lg font-bold text-foreground">
                                {formatOverdueDays(group.oldest_transaction.days_overdue)}
                              </p>
                            </div>

                            <div className={cn(
                              "rounded-lg p-3 border",
                              group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "bg-destructive/10 border-destructive/20" :
                              group.client.risk_level === RiskLevel.MEDIUM ? "bg-amber-500/10 border-amber-500/20" :
                              "bg-success/10 border-success/20"
                            )}>
                              <div className="flex items-center gap-2 mb-1">
                                <TrendingUp className={cn(
                                  "h-4 w-4",
                                  group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "text-destructive" :
                                  group.client.risk_level === RiskLevel.MEDIUM ? "text-amber-600 dark:text-amber-400" :
                                  "text-success"
                                )} />
                                <span className={cn(
                                  "text-xs font-medium",
                                  group.client.risk_level === RiskLevel.HIGH || group.client.risk_level === RiskLevel.VERY_HIGH ? "text-destructive" :
                                  group.client.risk_level === RiskLevel.MEDIUM ? "text-amber-600 dark:text-amber-400" :
                                  "text-success"
                                )}>Nível de risco</span>
                              </div>
                              <p className={cn(
                                "text-lg font-bold capitalize",
                                "text-foreground"
                              )}>
                                {group.client.risk_level === RiskLevel.VERY_HIGH ? 'Muito Alto' :
                                 group.client.risk_level === RiskLevel.HIGH ? 'Alto' :
                                 group.client.risk_level === RiskLevel.MEDIUM ? 'Médio' : 'Baixo'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="pt-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-9 w-9 p-0 hover:bg-muted">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => onRowClick?.(group)}>
                                <User className="h-4 w-4 mr-2" />
                                Ver perfil completo
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              {group.client.email && (
                                <DropdownMenuItem onClick={() => onContactClient?.(group.client.id, 'email')}>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Enviar cobrança por email
                                </DropdownMenuItem>
                              )}

                              {group.client.phone && (
                                <>
                                  <DropdownMenuItem onClick={() => onContactClient?.(group.client.id, 'phone')}>
                                    <Phone className="h-4 w-4 mr-2" />
                                    Realizar ligação
                                  </DropdownMenuItem>

                                  <DropdownMenuItem onClick={() => onContactClient?.(group.client.id, 'whatsapp')}>
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Enviar WhatsApp
                                  </DropdownMenuItem>
                                </>
                              )}

                              <DropdownMenuSeparator />

                              <DropdownMenuItem>
                                <Calendar className="h-4 w-4 mr-2" />
                                Criar plano de pagamento
                              </DropdownMenuItem>

                              <DropdownMenuItem>
                                <DollarSign className="h-4 w-4 mr-2" />
                                Aplicar desconto
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>

                {/* Expanded Transactions */}
                <CollapsibleContent>
                    <div className="border border-t-0 rounded-lg rounded-t-none overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Prioridade</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.transactions.map((transaction) => (
                            <TableRow
                              key={transaction.id}
                              className="hover:bg-muted/30 cursor-pointer"
                              onClick={() => {
                                setSelectedGroup(group);
                                setModalOpen(true);
                              }}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedTransactions.has(transaction.id)}
                                  onCheckedChange={(checked) =>
                                    onSelectTransaction(transaction.id, checked as boolean)
                                  }
                                  className="data-[state=checked]:bg-primary"
                                />
                              </TableCell>

                              <TableCell>
                                <div>
                                  <p className="font-medium">{transaction.description}</p>
                                  {transaction.installment_number && (
                                    <p className="text-xs text-muted-foreground">
                                      Parcela {transaction.installment_number}/{transaction.total_installments}
                                    </p>
                                  )}
                                  {transaction.contract_reference && (
                                    <p className="text-xs text-muted-foreground">
                                      Ref: {transaction.contract_reference}
                                    </p>
                                  )}
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span>{formatDate(transaction.due_date)}</span>
                                  <Badge variant="outline" className="text-xs">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {formatOverdueDays(transaction.days_overdue)}
                                  </Badge>
                                </div>
                              </TableCell>

                              <TableCell className="text-right">
                                <div>
                                  <p className="font-semibold text-finance-red">
                                    {formatCurrency(transaction.current_amount)}
                                  </p>
                                  {transaction.current_amount !== transaction.original_amount && (
                                    <p className="text-xs text-muted-foreground">
                                      Original: {formatCurrency(transaction.original_amount)}
                                    </p>
                                  )}
                                </div>
                              </TableCell>

                              <TableCell>
                                {getStatusBadge(transaction.status)}
                              </TableCell>

                              <TableCell>
                                {getPriorityBadge(transaction.priority)}
                              </TableCell>

                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedGroup(group);
                                      setModalOpen(true);
                                    }}>
                                      <FileText className="h-4 w-4 mr-2" />
                                      Ver detalhes
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <DollarSign className="h-4 w-4 mr-2" />
                                      Registrar pagamento
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <Calendar className="h-4 w-4 mr-2" />
                                      Renegociar prazo
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        group={selectedGroup}
        onPriorityChange={onPriorityChange}
        onStatusChange={onStatusChange}
        onSendNotice={onSendNotice}
        onAddNotes={onAddNotes}
        onMarkResolved={onMarkResolved}
        onScheduleFollowUp={onScheduleFollowUp}
      />
    </>
  );
}
